import { IConfigurations } from '@epicuro-next/platform/configuration';

export const PRODUCT_LIST_NAMESPACE = 'productlist';

/**
 * configuration keys for this module
 */
export enum ProductListConfigurationKeys {
  AccountsPastingLimit = 'accounts-pasting-limit',
  AllowEditionListsNotOwned = 'allow-edition-lists-not-owned',
  AllowEditOfCommercialListProducts = 'allow-edit-of-commercial-lists-products',
  AllowEmailPromotion = 'allow-email-promotion',
  AllowLookbooks = 'allow-lookbooks',
  AllowSpotlightPromotion = 'allow-spotlight-promotion',
  AllowModalPromotion = 'allow-modal-promotion',
  AllowLandingPagePromotion = 'allow-landing-page-promotion',
  BestListMinimumMonthsLimit = 'best-list-minimum-months-limit',
  ClickCreate = 'click-create',
  CommercialListLimit = 'commercial-list-limit',
  CommercialListMultiplierEnabled = 'commercial-list-multiplier',
  CommercialListToMdoEnabled = 'commercial-list-to-mdo-enabled',
  CommercialListValidationMessageDisplaySizeId = 'commercial-list-validation-message-display-size-id',
  CreateListMediaNotRequired = 'create-list-media-not-required',
  DefaultEmailManualInputRecipients = 'default-email-manual-input-recipients',
  DisabledUPCEANLeadingZero = 'disable-upcean-leading-zero',
  DisablePromotionNoProducts = 'disable-promotion-no-products',
  DynamicLayoutBuilderEnabled = 'dynamic-layout-builder-enabled',
  EnableBulkDelete = 'enable-bulk-delete',
  EnableCommercialProductList = 'enable-commercial-product-list',
  EnableCommercialProductListConsumption = 'enable-commercial-list-consumption',
  EnableCommercialProductListDateChange = 'enable-commercial-product-list-date-change',
  EnabledSinglePriceTypeLists = 'enable-single-price-type-lists',
  EnableGetInTouch = 'enable-get-in-touch',
  EnableListReachedEvent = 'enable-list-reached-event',
  EnableProductsSelectionEmails = 'enable-products-selection-emails',
  EnableRecipientsSelector = 'enable-recipients-selector',
  EnableRecipientsSelectorFilters = 'enable-recipients-selector-filters',
  EnableSlideListDeletion = 'enable-slide-list-deletion',
  EnableUrlShortener = 'enable-url-shortener',
  EnhancedListHeader = 'enable-enhanced-list-header',
  LinksUrlsWhitelist = 'links-urls-whitelist',
  ListOldBulkAddStyleIdInput = 'list-old-bulk-add-style-id-input',
  ListPageLayoutSchemas = 'list-page-layout-schemas',
  LookbookCreationTypeBlackList = 'lookbook-creation-type-blacklist',
  OrderTypeCommercialListAllowed = 'order-type-commercial-list-allowed',
  ProductAutocompletePreferMasterProductId = 'product-autocomplete-prefer-master-id',
  ProductIdFormat = 'list-product-id-format',
  ProductsAutocompleteLimit = 'products-autocomplete-limit',
  ProductTitleFormat = 'list-product-title-format',
  RenderStyleIDsOnPromotion = 'render-style-ids-on-promotion',
  ShopTheListDisabled = 'shop-the-list-disabled',
  ShowDescriptionEditor = 'show-description-editor',
  ShowDownloadDataButton = 'show-download-data-button',
  ShowDownloadImagesButton = 'show-download-images',
  ShowEpicShow = 'allowEpicShow',
  ShowGrouping = 'show-grouping',
  ShowLookBookNameInTitle = 'show-lookbook-name-in-title',
  ShowMediaScrollButtons = 'show-media-scroll-buttons',
  ShowProductsAutocomplete = 'show-products-autocomplete',
  ShowPromotionalIcons = 'show-promotional-icons',
  SupportedEmailTemplate = 'supportedEmailTemplates',
  SupportMultilineBulk = 'support-multiline-bulk',
  VideoUnmute = 'video-unmute',
  AllowExportGrouping = 'allow-export-grouping',
  MarkVariantInCsForOpenedList = 'mark-variant-in-cs-for-opened-list',
  BackgroundMediaSuggestionFolders = 'background-media-suggestion-folders',
  MediaSuggestionFolders = 'media-suggestion-folders',
}
/**
 * Configuration return types and default values for this module
 */
export interface ProductListConfigurations
  extends IConfigurations<ProductListConfigurationKeys> {
  [ProductListConfigurationKeys.AccountsPastingLimit]: number;
  [ProductListConfigurationKeys.AllowEditionListsNotOwned]: boolean;
  [ProductListConfigurationKeys.AllowEditOfCommercialListProducts]: boolean;
  [ProductListConfigurationKeys.AllowEmailPromotion]: boolean;
  [ProductListConfigurationKeys.AllowModalPromotion]: boolean;
  [ProductListConfigurationKeys.AllowLandingPagePromotion]: boolean;
  [ProductListConfigurationKeys.AllowLookbooks]: boolean;
  [ProductListConfigurationKeys.AllowSpotlightPromotion]: boolean;
  [ProductListConfigurationKeys.BestListMinimumMonthsLimit]: number;
  [ProductListConfigurationKeys.ClickCreate]: boolean;
  [ProductListConfigurationKeys.CommercialListLimit]: number;
  [ProductListConfigurationKeys.CommercialListMultiplierEnabled]: boolean;
  [ProductListConfigurationKeys.CommercialListToMdoEnabled]: boolean;
  [ProductListConfigurationKeys.CommercialListValidationMessageDisplaySizeId]: boolean;
  [ProductListConfigurationKeys.CreateListMediaNotRequired]: boolean;
  [ProductListConfigurationKeys.DefaultEmailManualInputRecipients]: boolean;
  [ProductListConfigurationKeys.DisabledUPCEANLeadingZero]: boolean;
  [ProductListConfigurationKeys.DisablePromotionNoProducts]: boolean;
  [ProductListConfigurationKeys.DynamicLayoutBuilderEnabled]: boolean;
  [ProductListConfigurationKeys.EnableBulkDelete]: boolean;
  [ProductListConfigurationKeys.EnableCommercialProductList]: boolean;
  [ProductListConfigurationKeys.EnableCommercialProductListConsumption]: boolean;
  [ProductListConfigurationKeys.EnableCommercialProductListDateChange]: boolean;
  [ProductListConfigurationKeys.EnabledSinglePriceTypeLists]: boolean;
  [ProductListConfigurationKeys.EnableGetInTouch]: boolean;
  [ProductListConfigurationKeys.EnableListReachedEvent]: boolean;
  [ProductListConfigurationKeys.EnableProductsSelectionEmails]: boolean;
  [ProductListConfigurationKeys.EnableRecipientsSelector]: boolean;
  [ProductListConfigurationKeys.EnableRecipientsSelectorFilters]: boolean;
  [ProductListConfigurationKeys.EnableSlideListDeletion]: boolean;
  [ProductListConfigurationKeys.EnableUrlShortener]: boolean;
  [ProductListConfigurationKeys.EnhancedListHeader]: boolean;
  [ProductListConfigurationKeys.LinksUrlsWhitelist]: string;
  [ProductListConfigurationKeys.ListOldBulkAddStyleIdInput]: boolean;
  [ProductListConfigurationKeys.ListPageLayoutSchemas]: string;
  [ProductListConfigurationKeys.LookbookCreationTypeBlackList]: string[];
  [ProductListConfigurationKeys.OrderTypeCommercialListAllowed]: boolean;
  [ProductListConfigurationKeys.ProductAutocompletePreferMasterProductId]: boolean;
  [ProductListConfigurationKeys.ProductIdFormat]: string[];
  [ProductListConfigurationKeys.ProductsAutocompleteLimit]: number;
  [ProductListConfigurationKeys.ProductTitleFormat]: string[];
  [ProductListConfigurationKeys.RenderStyleIDsOnPromotion]: boolean;
  [ProductListConfigurationKeys.ShopTheListDisabled]: boolean;
  [ProductListConfigurationKeys.ShowDescriptionEditor]: boolean;
  [ProductListConfigurationKeys.ShowDownloadDataButton]: boolean;
  [ProductListConfigurationKeys.ShowDownloadImagesButton]: boolean;
  [ProductListConfigurationKeys.ShowEpicShow]: boolean;
  [ProductListConfigurationKeys.ShowGrouping]: boolean;
  [ProductListConfigurationKeys.ShowLookBookNameInTitle]: boolean;
  [ProductListConfigurationKeys.ShowMediaScrollButtons]: boolean;
  [ProductListConfigurationKeys.ShowProductsAutocomplete]: boolean;
  [ProductListConfigurationKeys.ShowPromotionalIcons]: boolean;
  [ProductListConfigurationKeys.SupportedEmailTemplate]: string[];
  [ProductListConfigurationKeys.SupportMultilineBulk]: boolean;
  [ProductListConfigurationKeys.VideoUnmute]: boolean;
  [ProductListConfigurationKeys.AllowExportGrouping]: boolean;
  [ProductListConfigurationKeys.MarkVariantInCsForOpenedList]: boolean;
}
